import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, DocumentIcon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Cliente, PropuestaArchivo, PropuestaDetalle, Concepto } from '../types';
import proposalsService from '../services/proposals.service';
import clientsService from '../services/clients.service';
import conceptsService from '../services/concepts.service';
import { formatCurrency } from '../utils/currency';
import { useAuth } from '../contexts/AuthContext';


interface PropuestaFormData {
  titulo: string;
  descripcion: string;
  clienteId: number;
  comercialId: number;
  valorPropuesto?: number;
  validezHasta: string;
  estado: 'borrador' | 'en_proceso' | 'enviada' | 'aceptada' | 'rechazada' | 'cancelada' | 'vencida';
  archivos: PropuestaArchivo[];
  observaciones: string;
  detalles: Omit<PropuestaDetalle, 'id' | 'propuestaId'>[];
  fechaVencimiento?: string;
}

const PropuestaForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const clienteId = searchParams.get('clienteId');
  const isEditing = !!id;
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [conceptos, setConceptos] = useState<Concepto[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
  const [formData, setFormData] = useState<PropuestaFormData>({
    titulo: '',
    descripcion: '',
    clienteId: clienteId ? parseInt(clienteId) : 0,
    comercialId: 0,
    valorPropuesto: 0,
    validezHasta: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 días por defecto
    estado: 'borrador',
    archivos: [],
    observaciones: '',
    detalles: [{
      conceptoId: 0,
      descripcion: '',
      cantidad: 1,
      precioUnitario: 0,
      subtotalBruto: 0,
      montoDescuento: 0,
      subtotalNeto: 0
    }],
  } as PropuestaFormData);

  useEffect(() => {
    // Set comercialId from logged user when available and not editing
    if (user && !isEditing) {
      setFormData(prev => ({ ...prev, comercialId: user.id }));
    }

    const fetchData = async () => {
      try {
        const clientesData = await clientsService.getClientes(1, 1000);
        setClientes(clientesData.clientes || []);
        // Prefill client search when creating with clienteId from URL
        if (!isEditing && clienteId) {
          const selected = (clientesData.clientes || []).find((c: any) => c.id === parseInt(clienteId));
          if (selected) {
            setSearchTerm(`${selected.nombre}${selected.nit ? ` - ${selected.nit}` : ''}`);
          }
        }

        const conceptosData = await conceptsService.getConceptosActivos();
        setConceptos(conceptosData || []);

        if (isEditing && id) {
          const propuesta = await proposalsService.getPropuestaById(parseInt(id));
          setFormData({
            titulo: propuesta.titulo,
            descripcion: propuesta.descripcion,
            clienteId: propuesta.clienteId,
            comercialId: propuesta.comercialId,
            valorPropuesto: propuesta.valorPropuesto,
            validezHasta: propuesta.validezHasta ? propuesta.validezHasta.split('T')[0] : new Date().toISOString().split('T')[0],
            estado: propuesta.estado || 'borrador',
            archivos: Array.isArray(propuesta.archivos) ? propuesta.archivos : [],
            observaciones: propuesta.observaciones || '',
            detalles: (propuesta.detalles || []).map(detalle => ({
              conceptoId: detalle.conceptoId,
              descripcion: detalle.descripcion || '',
              cantidad: detalle.cantidad || 1,
              precioUnitario: detalle.precioUnitario ? Number(detalle.precioUnitario) : 0,
              descuentoPct: detalle.descuentoPct ? Number(detalle.descuentoPct) : undefined,
              subtotalBruto: detalle.subtotalBruto ? Number(detalle.subtotalBruto) : 0,
              montoDescuento: detalle.montoDescuento ? Number(detalle.montoDescuento) : 0,
              subtotalNeto: detalle.subtotalNeto ? Number(detalle.subtotalNeto) : 0
            })),
          });
          // Set client search text so dropdown shows the selected client
          const selectedCliente = (clientesData.clientes || []).find((c: any) => c.id === propuesta.clienteId);
          if (selectedCliente) {
            setSearchTerm(`${selectedCliente.nombre}${selectedCliente.nit ? ` - ${selectedCliente.nit}` : ''}`);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        alert('Error al cargar los datos');
      }
    };

    fetchData();
  }, [id, isEditing, user, clienteId]);

  // Filtra clientes basado en el término de búsqueda
  const filteredClientes = useMemo(() => {
    if (!searchTerm) return clientes;
    const term = searchTerm.toLowerCase();
    return clientes.filter(cliente => 
      cliente.nombre.toLowerCase().includes(term) || 
      (cliente.nit || '').toLowerCase().includes(term) ||
      (cliente.razonSocial || '').toLowerCase().includes(term)
    );
  }, [clientes, searchTerm]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'clienteId' ? parseInt(value) : 
               name === 'valorPropuesto' ? parseFloat(value.replace(/\./g, '').replace(',', '.')) || 0 : value
    }));
  };

  // Actualizar el valor propuesto cuando cambien los detalles
  useEffect(() => {
    const totalNeto = formData.detalles.reduce((sum, d) => sum + d.subtotalNeto, 0);
    setFormData(prev => ({
      ...prev,
      valorPropuesto: totalNeto
    }));
  }, [formData.detalles]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingFiles(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        // Aquí deberías implementar la lógica de subida de archivos
        // Por ahora, simulamos una respuesta exitosa
        const timestamp = Date.now();
        const s3Key = `propuestas/temp/${timestamp}_${file.name}`;
        
        // Simular subida de archivo
        await new Promise(resolve => setTimeout(resolve, 500));
        
        return {
          id: 0, // Temporal, será asignado por el backend
          propuestaId: 0, // Temporal, será asignado por el backend
          version: 1,
          filename: file.name,
          s3Key: s3Key,
          s3Url: `https://s3.amazonaws.com/bucket-name/${s3Key}`,
          createdAt: new Date().toISOString()
        } as PropuestaArchivo;
      });
      
      const uploadResults = await Promise.all(uploadPromises);
      
      setFormData(prev => ({
        ...prev,
        archivos: [...prev.archivos, ...uploadResults]
      }));
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Error al subir los archivos');
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleRemoveFile = (index: number) => {
    setFormData(prev => ({
      ...prev,
      archivos: prev.archivos.filter((_, i) => i !== index)
    }));
  };

  const handleAddDetalle = () => {
    setFormData(prev => ({
      ...prev,
      detalles: [
        ...prev.detalles,
        {
          conceptoId: 0,
          descripcion: '',
          cantidad: 1,
          precioUnitario: 0,
          subtotalBruto: 0,
          montoDescuento: 0,
          subtotalNeto: 0
        }
      ]
    }));
  };

  const handleRemoveDetalle = (index: number) => {
    if (formData.detalles.length <= 1) return; // No eliminar el último detalle
    
    setFormData(prev => ({
      ...prev,
      detalles: prev.detalles.filter((_, i) => i !== index)
    }));
  };

  const handleConceptoChange = (index: number, conceptoId: string) => {
    const concepto = conceptos.find((c: Concepto) => c.id === parseInt(conceptoId));
    if (concepto) {
      setFormData(prev => {
        const nuevosDetalles = [...prev.detalles];
        const detalleActualizado = {
          ...nuevosDetalles[index],
          conceptoId: concepto.id,
          descripcion: concepto.nombre,
          precioUnitario: concepto.precioLista,
        };
        
        // Recalcular subtotales
        const cantidad = detalleActualizado.cantidad;
        const precioUnitario = concepto.precioLista;
        const descuentoPct = detalleActualizado.descuentoPct || 0;
        
        const subtotalBruto = cantidad * precioUnitario;
        const montoDescuento = subtotalBruto * (descuentoPct / 100);
        const subtotalNeto = subtotalBruto - montoDescuento;
        
        detalleActualizado.subtotalBruto = subtotalBruto;
        detalleActualizado.montoDescuento = montoDescuento;
        detalleActualizado.subtotalNeto = subtotalNeto;
        
        nuevosDetalles[index] = detalleActualizado;
        
        return {
          ...prev,
          detalles: nuevosDetalles
        };
      });
    }
  };

  const handleDetalleChange = (index: number, field: string, value: any) => {
    setFormData(prev => {
      const nuevosDetalles = [...prev.detalles];
      let coerced: any = value;
      if (field === 'cantidad') {
        coerced = Number(value) || 0;
      } else if (field === 'precioUnitario') {
        coerced = Number(value) || 0;
      } else if (field === 'descuentoPct') {
        coerced = value === '' || value === null || value === undefined ? undefined : Number(value);
      }

      const detalleActualizado: any = { ...nuevosDetalles[index], [field]: coerced };

      // Calcular subtotales si se actualiza cantidad, precio o descuento
      if (['cantidad', 'precioUnitario', 'descuentoPct'].includes(field)) {
        const cantidad = Number(detalleActualizado.cantidad) || 0;
        const precioUnitario = Number(detalleActualizado.precioUnitario) || 0;
        const descuentoPct = Number(detalleActualizado.descuentoPct || 0) || 0;

        const subtotalBruto = cantidad * precioUnitario;
        const montoDescuento = subtotalBruto * (descuentoPct / 100);
        const subtotalNeto = subtotalBruto - montoDescuento;

        detalleActualizado.subtotalBruto = subtotalBruto;
        detalleActualizado.montoDescuento = montoDescuento;
        detalleActualizado.subtotalNeto = subtotalNeto;
      }

      nuevosDetalles[index] = detalleActualizado;

      return {
        ...prev,
        detalles: nuevosDetalles
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validaciones básicas
      const titulo = (formData.titulo || '').trim();
      if (!titulo) {
        alert('El título es requerido');
        return;
      }
      if (!formData.clienteId || formData.clienteId <= 0) {
        alert('Debes seleccionar un cliente');
        return;
      }
      if (!formData.detalles || formData.detalles.length === 0) {
        alert('Debes agregar al menos un detalle');
        return;
      }

      // Normalizar detalles al DTO del backend
      const detalles = formData.detalles.map(d => {
        const descripcion = (d.descripcion || '').trim();
        const cantidad = Number(d.cantidad) || 0;
        const precioUnitario = Number(d.precioUnitario) || 0;
        const rawDesc = (d as any).descuentoPct;
        const descuentoPct = rawDesc === undefined || rawDesc === null || rawDesc === ''
          ? undefined
          : Number(rawDesc);
        return {
          conceptoId: d.conceptoId,
          descripcion,
          cantidad,
          precioUnitario,
          descuentoPct,
        };
      });

      // Validar detalles
      for (const d of detalles) {
        if (!d.descripcion) {
          alert('Cada detalle debe tener descripción');
          return;
        }
        if (!Number.isFinite(d.cantidad) || d.cantidad <= 0) {
          alert('Cada detalle debe tener cantidad > 0');
          return;
        }
        if (!Number.isFinite(d.precioUnitario) || d.precioUnitario < 0) {
          alert('Cada detalle debe tener precio unitario válido');
          return;
        }
      }

      const effectiveComercialId = formData.comercialId || (user?.id || 0);
      if (!effectiveComercialId) {
        alert('No se encontró un comercial válido. Inicia sesión nuevamente.');
        return;
      }

      const submitData = {
        clienteId: formData.clienteId,
        comercialId: effectiveComercialId,
        titulo,
        descripcion: (formData.descripcion || '').trim() || undefined,
        validezHasta: formData.validezHasta ? new Date(formData.validezHasta).toISOString() : undefined,
        observaciones: (formData.observaciones || '').trim() || undefined,
        valorPropuesto: detalles.reduce((sum, d) => sum + (d.cantidad * d.precioUnitario * (1 - (d.descuentoPct || 0) / 100)), 0),
        estado: formData.estado,
        isActive: true,
        detalles,
      } as const;

      if (isEditing && id) {
        await proposalsService.updatePropuesta(parseInt(id), submitData as any);
      } else {
        await proposalsService.createPropuesta(submitData as any);
      }
      navigate('/propuestas');
    } catch (error) {
      console.error('Error saving propuesta:', error);
      const anyErr: any = error as any;
      const msg = anyErr?.response?.data?.message || anyErr?.message || 'Error al guardar la propuesta';
      alert(Array.isArray(msg) ? msg.join('\n') : msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/propuestas')}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Volver a propuestas
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditing ? 'Editar Propuesta' : 'Nueva Propuesta'}
        </h1>
        <p className="mt-2 text-sm text-gray-700">
          {isEditing ? 'Actualiza la información de la propuesta' : 'Crea una nueva propuesta comercial'}
        </p>
      </div>

      {/* Form */}
      <div className="max-w-4xl">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Información Básica */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">
              Información Básica
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label htmlFor="titulo" className="block text-sm font-medium text-gray-700 mb-2">
                  Título de la Propuesta *
                </label>
                <input
                  type="text"
                  id="titulo"
                  name="titulo"
                  required
                  value={formData.titulo}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Título descriptivo de la propuesta"
                />
              </div>
              
              <div className="relative">
                <label htmlFor="clienteSearch" className="block text-sm font-medium text-gray-700 mb-2">
                  Cliente *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="clienteSearch"
                    name="clienteSearch"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => setIsClientDropdownOpen(true)}
                    onBlur={() => setTimeout(() => setIsClientDropdownOpen(false), 200)}
                    placeholder="Buscar cliente..."
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  {isClientDropdownOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                      {filteredClientes.length > 0 ? (
                        filteredClientes.map((cliente) => (
                          <div
                            key={cliente.id}
                            className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100 ${
                              formData.clienteId === cliente.id ? 'bg-primary-50' : ''
                            }`}
                            onMouseDown={() => {
                              setFormData(prev => ({
                                ...prev,
                                clienteId: cliente.id
                              }));
                              setSearchTerm(`${cliente.nombre}${cliente.nit ? ` - ${cliente.nit}` : ''}`);
                              setIsClientDropdownOpen(false);
                            }}
                          >
                            <div className="flex items-center">
                              <span className="font-normal block truncate">
                                {cliente.nombre}
                                {cliente.nit && ` - ${cliente.nit}`}
                              </span>
                            </div>
                            {cliente.razonSocial && (
                              <span className="font-normal text-xs text-gray-500 block truncate">
                                {cliente.razonSocial}
                              </span>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500">
                          No se encontraron clientes
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <input
                  type="hidden"
                  name="clienteId"
                  value={formData.clienteId}
                  required
                />
              </div>

              <div>
                <label htmlFor="valorPropuesto" className="block text-sm font-medium text-gray-700 mb-2">
                  Valor Propuesto *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                  <input
                    type="text"
                    id="valorPropuesto"
                    name="valorPropuesto"
                    required
                    readOnly
                    value={formatCurrency(formData.valorPropuesto || 0)}
                    className="block w-full pl-8 pr-3 py-2 border border-gray-300 bg-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="validezHasta" className="block text-sm font-medium text-gray-700 mb-2">
                  Válido hasta *
                </label>
                <input
                  type="date"
                  id="validezHasta"
                  name="validezHasta"
                  required
                  min={new Date().toISOString().split('T')[0]}
                  value={formData.validezHasta}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label htmlFor="fechaVencimiento" className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Vencimiento
                </label>
                <input
                  type="date"
                  id="fechaVencimiento"
                  name="fechaVencimiento"
                  value={formData.fechaVencimiento}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label htmlFor="estado" className="block text-sm font-medium text-gray-700 mb-2">
                  Estado *
                </label>
                <select
                  id="estado"
                  name="estado"
                  required
                  value={formData.estado}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="borrador">Borrador</option>
                  <option value="en_proceso">En Proceso</option>
                  <option value="enviada">Enviada</option>
                  <option value="aceptada">Aceptada</option>
                  <option value="rechazada">Rechazada</option>
                  <option value="cancelada">Cancelada</option>
                  <option value="vencida">Vencida</option>
                </select>
              </div>
            </div>

            {/* Detalles de la propuesta */}
            <div className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Detalles de la propuesta</h3>
                <button
                  type="button"
                  onClick={handleAddDetalle}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Agregar línea
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-64">Concepto</th>
                      <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descripción</th>
                      <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Cant.</th>
                      <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Precio Unit.</th>
                      <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Desc. %</th>
                      <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Subtotal</th>
                      <th scope="col" className="relative px-3 py-3 w-16">
                        <span className="sr-only">Acciones</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {formData.detalles.map((detalle, index) => (
                      <tr key={index}>
                        <td className="px-3 py-2">
                          <select
                            value={detalle.conceptoId || ''}
                            onChange={(e) => handleConceptoChange(index, e.target.value)}
                            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          >
                            <option value="">Seleccionar concepto...</option>
                            {conceptos.map((concepto) => (
                              <option key={concepto.id} value={concepto.id}>
                                {concepto.codigo ? `${concepto.codigo} - ` : ''}{concepto.nombre}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={detalle.descripcion}
                            onChange={(e) => handleDetalleChange(index, 'descripcion', e.target.value)}
                            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                            placeholder="Descripción del servicio"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="1"
                            max="9999"
                            step="1"
                            value={detalle.cantidad}
                            onChange={(e) => handleDetalleChange(index, 'cantidad', e.target.value)}
                            className="block w-full text-right border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={detalle.precioUnitario.toLocaleString('es-CO')}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^0-9]/g, '');
                              handleDetalleChange(index, 'precioUnitario', value);
                            }}
                            className="block w-full text-right border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                            placeholder="0"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={detalle.descuentoPct || ''}
                            onChange={(e) => handleDetalleChange(index, 'descuentoPct', e.target.value)}
                            className="block w-full text-right border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            placeholder="0"
                          />
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right text-sm text-gray-900">
                          ${detalle.subtotalNeto.toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right text-sm font-medium">
                          {formData.detalles.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveDetalle(index)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Eliminar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50">
                      <td colSpan={4} className="px-3 py-2 text-right text-sm font-medium text-gray-900">
                        Total Bruto:
                      </td>
                      <td className="px-3 py-2 text-right text-sm font-medium text-gray-900">
                        ${formData.detalles.reduce((sum, d) => sum + d.subtotalBruto, 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td></td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td colSpan={4} className="px-3 py-2 text-right text-sm font-medium text-gray-900">
                        Descuento Total:
                      </td>
                      <td className="px-3 py-2 text-right text-sm font-medium text-gray-900">
                        -${formData.detalles.reduce((sum, d) => sum + d.montoDescuento, 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td></td>
                    </tr>
                    <tr className="bg-gray-100 border-t-2 border-gray-200">
                      <td colSpan={4} className="px-3 py-2 text-right text-base font-bold text-gray-900">
                        Total Neto:
                      </td>
                      <td className="px-3 py-2 text-right text-base font-bold text-gray-900">
                        ${formData.detalles.reduce((sum, d) => sum + d.subtotalNeto, 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-6">
              <label htmlFor="descripcion" className="block text-sm font-medium text-gray-700 mb-2">
                Descripción *
              </label>
              <textarea
                id="descripcion"
                name="descripcion"
                required
                rows={4}
                value={formData.descripcion}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Descripción detallada de la propuesta"
              />
            </div>

            <div className="mt-6">
              <label htmlFor="observaciones" className="block text-sm font-medium text-gray-700 mb-2">
                Observaciones
              </label>
              <textarea
                id="observaciones"
                name="observaciones"
                rows={3}
                value={formData.observaciones}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Observaciones adicionales"
              />
            </div>
          </div>

          {/* Archivos */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">
              Archivos Adjuntos
            </h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subir Archivos
              </label>
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                onChange={handleFileUpload}
                disabled={uploadingFiles}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
              />
              <p className="mt-2 text-xs text-gray-500">
                Formatos permitidos: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX
              </p>
              {uploadingFiles && (
                <p className="mt-2 text-sm text-blue-600">Subiendo archivos...</p>
              )}
            </div>

            {/* Lista de archivos */}
            {formData.archivos.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-gray-700">Archivos adjuntos:</h3>
                {formData.archivos.map((archivo, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                    <div className="flex items-center">
                      <DocumentIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-700">
            {archivo.filename?.split('.').pop()?.toUpperCase() || 'ARCHIVO'} - {archivo.filename || `Archivo ${index + 1}`}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/propuestas')}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || uploadingFiles}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear Propuesta')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PropuestaForm;